import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  adminSidebarMenu,
  userSidebarMenu as baseUserSidebarMenu,
} from "../../config/sidebarMenuConfig";
import { ChevronDownIcon, ChevronRightIcon } from "../../icons/CommonIcons";
import dashboardImg from "../../assets/images/dashboard-logo.png";
import Image from "./Image.jsx";
import Input from "./Input.jsx";
import { getStoredItem, setStoredItem } from "../../config/storageUtils.js";
import api from "../../api/ApiService.js";
import { canShowApplyNew } from "../../utils/applicationUtils.js";
import { useLanguage } from "../LanguageProvider/LanguageProvider.jsx";
import { useAuth } from "../Auth/AuthProvider.jsx";

const Sidebar = () => {
  const [openMenus, setOpenMenus] = useState({});
  const [userSidebarMenu, setUserSidebarMenu] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [role, setRole] = useState(null);
  const { userType, setUserType } = useAuth() || "user";
  const { language, setLanguage } = useLanguage();

  const roleMenuMap = {
    1: "/admin/tifc/applications",
    2: "/admin/dlct/applications",
    3: "/admin/slac/applications",
    4: "/admin/ddo/applications",
  };

  useEffect(() => {
    const getUserDetails = async () => {
      const resp = await api.get("/user-full-data");
      if (resp.status === 200) {
        setRole(resp?.data?.user_details?.role_id);
      }
    };
    getUserDetails();
  }, []);

  // Fetch user applications if role is user
  useEffect(() => {
    const fetchUserApplications = async () => {
      if (!userType) return setUserSidebarMenu(baseUserSidebarMenu);

      try {
        const response = await api.get(`/get-user-applications`);
        const applications = response.data || [];
        const showApplyMenu = canShowApplyNew(applications);

        const updatedMenu = baseUserSidebarMenu.filter(
          (item) => showApplyMenu || item.path !== "/apply-incentive-claim",
        );

        setUserSidebarMenu((prev) => {
          const prevIds = prev.map((i) => i.path).join(",");
          const newIds = updatedMenu.map((i) => i.path).join(",");
          return prevIds === newIds ? prev : updatedMenu;
        });
      } catch (error) {
        console.error("Error fetching applications:", error);
        setUserSidebarMenu(baseUserSidebarMenu);
      }
    };

    if (userType === "user") fetchUserApplications();
  }, [userType]);

  // Build sidebar menu based on role
  const sidebarMenu = useMemo(() => {
    if (userType === "admin" || userType === "superadmin") {
      if (["1", "2", "3", "4"].includes(String(role))) {
        const allowedPath = roleMenuMap[String(role)];
        return adminSidebarMenu.filter(
          (menu) =>
            menu.path === allowedPath ||
            menu.children?.some((child) => child.path === allowedPath) ||
            menu.path === "/admin/registered-users",
        );
      }
      return adminSidebarMenu;
    } else {
      return userSidebarMenu;
    }
  }, [role, userType, userSidebarMenu]);

  // Open parent menus if current path matches child
  useEffect(() => {
    const openParents = {};
    sidebarMenu.forEach((menu) => {
      if (menu.children?.some((child) => child.path === currentPath)) {
        openParents[menu.title] = true;
      }
    });
    setOpenMenus(openParents);
  }, [currentPath, sidebarMenu]);

  const toggleMenu = (menuTitle) =>
    setOpenMenus((prev) => ({ ...prev, [menuTitle]: !prev[menuTitle] }));

  const handleMenuClick = (menu) => {
    if (menu.children) toggleMenu(menu.title.en);
    else navigate(menu.path);
  };

  return (
    <aside
      id="sidebar"
      className="fixed h-screen overflow-y-auto custom-scrollbar md:relative z-20 
w-[376px] max-w-[376px] shrink-0 
bg-white shadow-md transform -translate-x-full md:translate-x-0 
transition-transform duration-300 ease-in-out"
    >
      <div className="p-4 border-b">
        <Image src={dashboardImg} alt="Logo" className="h-10 mb-4" />
        <Input
          type="text"
          placeholder={language ? "खोजें" : "Search"}
          className="w-full px-3 py-2 border rounded-md text-sm"
        />
      </div>

      <nav className="mt-4 px-4 space-y-2">
        {sidebarMenu.map((menu) => {
          const Icon = menu.icon;
          const isOpen = openMenus[menu.title.en];
          const isActiveParent =
            currentPath === menu.path ||
            menu.children?.some((c) => c.path === currentPath);

          return (
            <div key={menu.title.en}>
              <button
                onClick={() => handleMenuClick(menu)}
                className={`flex items-center justify-between w-full py-2 px-3 rounded transition-colors duration-200
                  ${
                    isActiveParent ? "nav-bg text-white" : "hover:bg-gray-100"
                  }`}
              >
                <span
                  className={`flex items-center gap-2 ${
                    isActiveParent ? "text-white" : ""
                  }`}
                >
                  {Icon && (isActiveParent ? <Icon fill="white" /> : <Icon />)}
                  {language ? menu.title?.hi : menu.title?.en}
                </span>
                {menu.children && (
                  <span>
                    {isOpen ? (
                      <ChevronDownIcon />
                    ) : (
                      <ChevronRightIcon
                        fill={isActiveParent ? "white" : undefined}
                      />
                    )}
                  </span>
                )}
              </button>

              {menu.children && isOpen && (
                <div className="ml-6 mt-1 space-y-1">
                  {menu.children.map((child) => {
                    const isChildActive = currentPath === child.path;
                    const ChildIcon = child.icon;
                    return (
                      <button
                        key={child.path}
                        onClick={() => navigate(child.path)}
                        className={`flex items-center gap-2 py-2 px-3 rounded w-full transition-colors duration-150
                          ${
                            isChildActive
                              ? "nav-bg text-white font-bold"
                              : "hover:bg-gray-100"
                          }`}
                      >
                        {ChildIcon &&
                          (isChildActive ? (
                            <ChildIcon fill="white" />
                          ) : (
                            <ChildIcon />
                          ))}
                        {language ? child.title.hi : child.title.en}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
